import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { paymentsApi } from '../lib/api';
import { CheckCircle, Clock, XCircle, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';

const MAX_POLLS = 6;
const POLL_INTERVAL = 2500;

export default function RegistrationSuccess() {
    const [params] = useSearchParams();
    const sessionId = params.get('session_id');
    const [status, setStatus] = useState('loading');
    const [attempt, setAttempt] = useState(0);

    const pollStatus = useCallback(async (att) => {
        if (!sessionId) { setStatus('no_session'); return; }
        try {
            const { data } = await paymentsApi.getStatus(sessionId);
            if (data.payment_status === 'paid') {
                setStatus('success');
            } else if (data.status === 'expired') {
                setStatus('expired');
            } else if (att >= MAX_POLLS) {
                setStatus('pending');
            } else {
                setAttempt(att + 1);
            }
        } catch {
            if (att >= MAX_POLLS) setStatus('error');
            else setAttempt(att + 1);
        }
    }, [sessionId]);

    useEffect(() => {
        if (attempt === 0) {
            pollStatus(0);
        } else if (attempt > 0 && attempt <= MAX_POLLS) {
            const t = setTimeout(() => pollStatus(attempt), POLL_INTERVAL);
            return () => clearTimeout(t);
        }
    }, [attempt, pollStatus]);

    const content = {
        loading: { icon: Loader2, color: 'brand-icon animate-spin', title: 'Verifying payment...', desc: 'Please wait while we confirm your payment with Stripe.' },
        success: { icon: CheckCircle, color: 'text-emerald-400', title: 'Registration Complete!', desc: 'Your subscription is active. You can now sign in and start managing food safety compliance.' },
        pending: { icon: Clock, color: 'text-amber-400', title: 'Payment Processing', desc: 'Your payment is still being processed. You can sign in once it is confirmed — this usually takes less than a minute.' },
        expired: { icon: XCircle, color: 'text-rose-400', title: 'Session Expired', desc: 'Your payment session has expired. Please try registering again.' },
        error: { icon: XCircle, color: 'text-rose-400', title: 'Something went wrong', desc: 'We could not verify your payment. Please contact support or try again.' },
        no_session: { icon: XCircle, color: 'text-rose-400', title: 'Invalid session', desc: 'No payment session found.' },
    }[status];

    const Icon = content.icon;

    return (
        <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4">
            <div className="text-center max-w-md" data-testid="registration-success-page">
                <Icon className={`h-16 w-16 mx-auto mb-4 ${content.color}`} />
                <h1 className="text-2xl font-bold text-slate-100 mb-2">{content.title}</h1>
                <p className="text-slate-400 mb-6">{content.desc}</p>
                {(status === 'success' || status === 'pending') && (
                    <Link to="/login">
                        <Button className="brand-btn font-semibold" data-testid="go-to-login-button">
                            Go to Sign In <ArrowRight className="h-4 w-4 ml-2" />
                        </Button>
                    </Link>
                )}
                {status === 'expired' && (
                    <Link to="/register">
                        <Button className="brand-btn font-semibold" data-testid="try-again-button">
                            Try Again <ArrowRight className="h-4 w-4 ml-2" />
                        </Button>
                    </Link>
                )}
            </div>
        </div>
    );
}
